// Wrapper component for displaying a list of items that can be sorted using dragging/dropping
import { CPText, Container } from "shared/components/base";
import * as React from "react";
import {
  Sortable,
  SortableItemUIProps,
  SortableOnDragOverEvent,
} from "@progress/kendo-react-sortable";
import "./CPSortableListStyles.scss";
import CPFontAwesome from "../CPFontAwesome/CPFontAwesome";
import classNames from "classnames";

export interface CPSortableListItem {
  /** Unique identifier for the item used by the Kendo Sortable engine. */
  key: string | number;
  /** Display text shown inside each sortable row. */
  text?: string;
  /** Whether the item is currently being dragged (managed internally). */
  isActive?: boolean;
}

interface CPSortableListProps {
  /** Array of items to render in the sortable list. */
  data: CPSortableListItem[];
  /** Callback fired on every drag-over event with the reordered array. Update your state here. */
  onSort?: (items: CPSortableListItem[]) => void;
}

/**
 * Drag-and-drop sortable list wrapping the Kendo `Sortable` component.
 * Each row displays a drag handle (bars icon) and the item's `text`.
 *
 * @example
 * ```tsx
 * const [items, setItems] = React.useState([
 *   { key: 1, text: 'First' },
 *   { key: 2, text: 'Second' },
 *   { key: 3, text: 'Third' },
 * ]);
 *
 * <CPSortableList data={items} onSort={setItems} />
 * ```
 */
const CPSortableList: React.FC<CPSortableListProps> = (props) => {
  const renderSortableItemUI = (props: SortableItemUIProps) => {
    const { attributes, dataItem, forwardRef, isActive, style } = props;

    return (
      <div
        ref={forwardRef}
        {...attributes}
        className={classNames("cp-sortable-list-item", { active: isActive })}
        style={style}
      >
        <CPFontAwesome icon="fa-solid fa-bars" />
        <CPText>{dataItem.text}</CPText>
      </div>
    );
  };

  const onDragOver = (event: SortableOnDragOverEvent) => {
    if (props.onSort) {
      props.onSort(event.newState as CPSortableListItem[]);
    }
  };

  return (
    <Container>
      <Sortable
        className="cp-sortable-list"
        idField={"key"}
        data={props.data}
        itemUI={renderSortableItemUI}
        onDragOver={onDragOver}
      />
    </Container>
  );
};

export default CPSortableList;
